import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import useDataImport from '../../hooks/useDataImport'
import { Check, ExpandMore, UploadFile } from '@mui/icons-material'

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant='determinate' {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant='body2' color='text.secondary'>{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  )
}

const CustomerImportWorker = () => {
  let inputRef

  const {
    handleFileUpload,
    status,
    progress,
    localErrors,
    fatalError,
    validationErrors,
    hasValidationErrors,
    abort,
    commitFileUpload,
    continueFun,
  } = useDataImport()

  const handleSelectFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  return (
    <Box>
      {(status === 'waiting' || status === 'aborted') && (
        <>
          <Button
            onClick={() => inputRef.click()}
            endIcon={<UploadFile />}
            variant='contained'
          >
            Import
          </Button>
          <input
            hidden
            type='file'
            onChange={handleSelectFile}
            ref={(fileInputRef) => (inputRef = fileInputRef)}
            accept='.csv'
          />
        </>
      )}

      {status === 'aborted' && (
        <>
          <Typography sx={{ mt: 2 }} color={'error'}>
            Fatal error: {fatalError?.message}
          </Typography>
        </>
      )}

      {['uploading', 'complete', 'validating'].includes(status) && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ mr: 2 }}>
                {status === 'uploading' && !continueFun && 'Uploading'}
                {(status === 'complete' || status === 'validating') &&
                  'Upload complete'}
              </Typography>
              {status === 'uploading' && !continueFun && (
                <CircularProgress size={'1rem'} />
              )}
              {status === 'complete' && <Check size={'1rem'} color='success' />}
            </Box>
            <LinearProgressWithLabel value={progress * 100} />
          </Box>

          {status === 'validating' && !hasValidationErrors && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 2 }}>Validating uploaded data</Typography>
              <CircularProgress size={'1rem'} />
            </Box>
          )}

          {status === 'validating' && hasValidationErrors && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ mr: 2 }}>
                  Encountered errors during validation
                </Typography>
                <Box>
                  <Button onClick={abort} variant={'outlined'} sx={{ mr: 1 }}>
                    Abort
                  </Button>

                  <Button
                    onClick={commitFileUpload}
                    variant={'contained'}
                    color={'warning'}
                  >
                    Discard invalid data and finish
                  </Button>
                </Box>
              </Box>

              {validationErrors?.unresolved?.length > 0 && (
                <Accordion elevation={8}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ mr: 2 }}>
                      Could not find corresponding values in the system for
                      these provided values (
                      {validationErrors.unresolved.length > 100
                        ? '100+'
                        : validationErrors.unresolved.length}
                      )
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer sx={{ mb: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Unknown values</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {validationErrors.unresolved
                            .slice(0, 100)
                            .map((error, i) => {
                              return (
                                <TableRow key={i}>
                                  <TableCell>{error.columnName}</TableCell>
                                  <TableCell>
                                    {error.providedValues.join(', ')}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {validationErrors?.invalidType?.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ mr: 2 }}>
                      These provided values do not fit the required data type (
                      {validationErrors.invalidType.length > 100
                        ? '100+'
                        : validationErrors.invalidType.length}
                      )
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Account name</TableCell>
                            <TableCell>Field</TableCell>
                            <TableCell>Provided value</TableCell>
                            <TableCell>Expected data type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {validationErrors?.invalidType
                            .slice(0, 100)
                            .map((error, i) => {
                              return (
                                <TableRow key={i}>
                                  <TableCell>{error.name}</TableCell>
                                  <TableCell>{error.columnName}</TableCell>
                                  <TableCell>{error.providedValue}</TableCell>
                                  <TableCell>
                                    {error.expectedDataType}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}

          {continueFun && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 2 }}>
                  Encountered {localErrors.length} error(s). These rows may be
                  discarded
                </Typography>

                <Button onClick={abort} variant={'outlined'} sx={{ mr: 1 }}>
                  Abort
                </Button>

                <Button
                  onClick={continueFun}
                  variant={'contained'}
                  color={'warning'}
                >
                  Ignore and continue
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row #</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localErrors.slice(0, 100).map((error, i) => {
                      return (
                        <TableRow key={i}>
                          <TableCell>{error.row}</TableCell>
                          <TableCell>{error.message}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default CustomerImportWorker
